<template>
  <div>
    <div class="mfg-header">
      <h4 class="mfg-title">Lots &amp; Quality</h4>
    </div>

    <div class="filter-bar">
      <input type="text" class="form-control" v-model="filters.search" @input="debouncedLoad" placeholder="Lot or supplier lot number" style="flex:1" />
      <select class="custom-select" v-model="filters.status" @change="load" style="max-width:160px">
        <option value="">All statuses</option>
        <option value="quarantined">Quarantined</option>
        <option value="released">Released</option>
        <option value="rejected">Rejected</option>
      </select>
      <div class="custom-control custom-checkbox ml-2 align-self-center">
        <input type="checkbox" class="custom-control-input" id="lotsInStock" v-model="filters.in_stock" @change="load" />
        <label class="custom-control-label" for="lotsInStock">In stock</label>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>
    <div v-else class="table-responsive mfg-box">
      <table class="table table-hover mb-0">
        <thead>
          <tr>
            <th>Lot</th><th>Product</th><th>Supplier lot</th><th class="text-right">Remaining</th><th>Expiry</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lot in lots" :key="lot.id" @click="open(lot)" style="cursor:pointer">
            <td class="font-weight-bold">{{ lot.lot_number }}</td>
            <td>{{ lot.product && lot.product.product }}</td>
            <td>{{ lot.supplier_lot_number || '—' }}</td>
            <td class="text-right">{{ qty(lot.quantity_remaining) }} {{ unitOf(lot.product) }}</td>
            <td>{{ lot.expiry_date ? lot.expiry_date.substring(0, 10) : '—' }}</td>
            <td><span class="badge" :class="statusBadge(lot.status)">{{ lot.status }}</span></td>
          </tr>
          <tr v-if="!lots.length"><td colspan="6" class="text-center text-muted py-4">No lots. Lots are created when a lot-tracked product is purchased or produced.</td></tr>
        </tbody>
      </table>
    </div>

    <b-modal ref="lotModal" :title="selected ? 'Lot ' + selected.lot_number : 'Lot'" size="lg" hide-footer>
      <div v-if="selected">
        <div class="row mb-4 pb-2 mfg-kpis">
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Product</div><div class="font-weight-bold">{{ selected.product && selected.product.product }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Status</div><span class="badge" :class="statusBadge(selected.status)">{{ selected.status }}</span></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Received / remaining</div><div class="font-weight-bold">{{ qty(selected.quantity_received) }} / {{ qty(selected.quantity_remaining) }} {{ unitOf(selected.product) }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Supplier lot</div><div class="font-weight-bold">{{ selected.supplier_lot_number || '—' }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Supplier</div><div>{{ selected.supplier ? selected.supplier.supplier : '—' }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Received</div><div>{{ selected.received_at ? selected.received_at.substring(0, 10) : '—' }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Expiry / retest</div><div>{{ selected.expiry_date ? selected.expiry_date.substring(0, 10) : '—' }} / {{ selected.retest_date ? selected.retest_date.substring(0, 10) : '—' }}</div></div>
          <div class="col-6 col-md-3" v-if="selected.status_reason"><div class="text-secondary small">Status reason</div><div>{{ selected.status_reason }}</div></div>
        </div>

        <h6 class="section-title">Quality checks</h6>
        <table class="table table-sm">
          <thead><tr><th>Test</th><th>Result</th><th>Limits</th><th>Outcome</th><th>By</th></tr></thead>
          <tbody>
            <tr v-for="c in selected.quality_checks" :key="c.id">
              <td>{{ c.test_name }}</td>
              <td>{{ c.result_value }} {{ c.unit }}</td>
              <td class="text-muted">{{ limits(c) }}</td>
              <td><span class="badge" :class="c.passed ? 'badge-primary' : 'badge-danger'">{{ c.passed ? 'Pass' : 'Fail' }}</span></td>
              <td class="text-muted small">{{ c.checked_by && c.checked_by.name }}</td>
            </tr>
            <tr v-if="!selected.quality_checks || !selected.quality_checks.length"><td colspan="5" class="text-muted">No checks recorded.</td></tr>
          </tbody>
        </table>

        <form class="form-row align-items-end mb-3" @submit.prevent="recordCheck">
          <div class="col-md-4 mb-2">
            <label class="small text-secondary mb-1">Test</label>
            <select class="custom-select" v-model="check.quality_test_id">
              <option value="">Other (type a name)</option>
              <option v-for="t in tests" :key="t.id" :value="t.id">{{ t.name }}{{ t.unit ? ' (' + t.unit + ')' : '' }}</option>
            </select>
          </div>
          <div class="col-md-3 mb-2" v-if="!check.quality_test_id">
            <label class="small text-secondary mb-1">Test name</label>
            <input class="form-control" v-model="check.test_name" />
          </div>
          <div class="col-md-2 mb-2">
            <label class="small text-secondary mb-1">Result</label>
            <input class="form-control" v-model="check.result_value" />
          </div>
          <div class="col-md-2 mb-2" v-if="!check.quality_test_id">
            <label class="small text-secondary mb-1">Outcome</label>
            <select class="custom-select" v-model="check.passed"><option :value="true">Pass</option><option :value="false">Fail</option></select>
          </div>
          <div class="col-md-1 mb-2"><button class="btn btn-primary btn-block" :disabled="busy || !check.result_value">Add</button></div>
        </form>

        <div class="form-group">
          <label class="small text-secondary mb-1">Reason (required to quarantine or reject)</label>
          <input class="form-control" v-model="statusReason" />
        </div>
        <div class="d-flex flex-wrap" style="gap:8px">
          <button class="btn btn-primary" :disabled="busy || selected.status === 'released'" @click="setStatus('released')">Release</button>
          <button class="btn btn-secondary" :disabled="busy || selected.status === 'quarantined'" @click="setStatus('quarantined')">Quarantine</button>
          <button class="btn btn-danger" :disabled="busy || selected.status === 'rejected'" @click="setStatus('rejected')">Reject</button>
          <button class="btn btn-outline-dark ml-auto" @click="$emit('trace', selected)"><i class="bi bi-diagram-3 mr-1"></i>Trace this lot</button>
        </div>
        <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>

        <h6 class="section-title mt-4">Movements</h6>
        <table class="table table-sm">
          <thead><tr><th>When</th><th>Type</th><th class="text-right">Qty</th><th>Notes</th></tr></thead>
          <tbody>
            <tr v-for="e in selected.ledger_entries" :key="e.id">
              <td class="small">{{ (e.created_at || '').substring(0, 16).replace('T', ' ') }}</td>
              <td class="small">{{ e.entry_type.replace(/_/g, ' ') }}</td>
              <td class="text-right" :class="e.quantity < 0 ? 'text-danger' : ''">{{ qty(e.quantity) }}</td>
              <td class="small text-muted">{{ e.notes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { apiError, fmtQty } from './mfgHelpers';

export default {
  name: 'LotsPanel',
  data() {
    return {
      lots: [], tests: [], loading: false, busy: false, error: null,
      filters: { search: '', status: '', in_stock: true },
      selected: null, statusReason: '',
      check: { quality_test_id: '', test_name: '', result_value: '', passed: true },
      timer: null,
    };
  },
  mounted() {
    this.load();
    axios.get('/api/manufacturing/quality-tests').then(({ data }) => { this.tests = data; }).catch(() => {});
  },
  methods: {
    qty: fmtQty,
    unitOf(p) { return p ? (p.measuring_unit || '') : ''; },
    statusBadge(s) { return s === 'released' ? 'badge-primary' : s === 'rejected' ? 'badge-danger' : 'badge-secondary'; },
    limits(c) {
      if (c.min_value == null && c.max_value == null) return '—';
      return (c.min_value != null ? '≥ ' + c.min_value : '') + (c.min_value != null && c.max_value != null ? ', ' : '') + (c.max_value != null ? '≤ ' + c.max_value : '');
    },
    debouncedLoad() { clearTimeout(this.timer); this.timer = setTimeout(this.load, 350); },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/manufacturing/lots', { params: { ...this.filters, in_stock: this.filters.in_stock ? 1 : 0 } });
        this.lots = data.data;
      } finally { this.loading = false; }
    },
    async open(lot) {
      this.error = null; this.statusReason = '';
      const { data } = await axios.get(`/api/manufacturing/lots/${lot.id}`);
      this.selected = data;
      this.$refs.lotModal.show();
    },
    async recordCheck() {
      this.busy = true; this.error = null;
      try {
        const payload = { ...this.check };
        if (payload.quality_test_id) { delete payload.test_name; delete payload.passed; } else { delete payload.quality_test_id; }
        const { data } = await axios.post(`/api/manufacturing/lots/${this.selected.id}/quality-checks`, payload);
        this.$toasts && this.$toasts.push({ type: data.check.passed ? 'success' : 'error', message: data.message });
        this.check = { quality_test_id: '', test_name: '', result_value: '', passed: true };
        await this.open(this.selected);
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
    async setStatus(status) {
      this.busy = true; this.error = null;
      try {
        const { data } = await axios.patch(`/api/manufacturing/lots/${this.selected.id}/status`, { status, reason: this.statusReason || null });
        this.$toasts && this.$toasts.push({ type: 'success', message: data.message });
        await this.open(this.selected);
        this.load();
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
  },
};
</script>
